import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Bell, Users2, Clock, Check, Radio } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import {
  fetchUserNotifications,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  respondToCollabRequest,
  formatNotificationTime,
  formatSpotifyArtistUrl,
  formatExternalUrl,
} from '../../lib/notificationService';
import { supabase, isSupabaseConfigured } from '../../lib/supabase';
import logo from '../../assets/SenritsuXwordmarkwhite.png';
import '../../styles/Home.css';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const notifRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, profile, isAuthenticated, signOut } = useAuth();

  // Scroll detection for blurred header
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Fetch notifications for the logged-in user
  useEffect(() => {
    let isMounted = true;
    async function loadNotifications() {
      if (user?.id) {
        try {
          const data = await fetchUserNotifications(user.id);
          if (data && isSupabaseConfigured()) {
            const senderIds = data
              .filter(
                (n) =>
                  n.type === 'collab_request' &&
                  n.data?.sender_id &&
                  (!n.data?.spotify_link || !n.data?.youtube || !n.data?.instagram)
              )
              .map((n) => n.data.sender_id);

            if (senderIds.length > 0) {
              try {
                const { data: senderProfiles } = await supabase
                  .from('profiles')
                  .select('id, spotify_link, youtube, instagram')
                  .in('id', [...new Set(senderIds)]);

                if (senderProfiles?.length) {
                  const spMap = Object.fromEntries(senderProfiles.map((p) => [p.id, p]));
                  data.forEach((n) => {
                    if (n.type === 'collab_request' && n.data?.sender_id && spMap[n.data.sender_id]) {
                      const sp = spMap[n.data.sender_id];
                      if (!n.data.spotify_link && sp.spotify_link) {
                        n.data.spotify_link = formatSpotifyArtistUrl(sp.spotify_link);
                      }
                      if (!n.data.youtube && sp.youtube) {
                        n.data.youtube = formatExternalUrl(sp.youtube, 'youtube.com');
                      }
                      if (!n.data.instagram && sp.instagram) {
                        n.data.instagram = formatExternalUrl(sp.instagram, 'instagram.com');
                      }
                    }
                  });
                }
              } catch {}
            }
          }
          if (isMounted) setNotifications(data);
        } catch (err) {
          console.warn('[Navbar] Notifs load notice:', err);
        }
      } else {
        if (isMounted) setNotifications([]);
      }
    }
    loadNotifications();

    // Poll periodically or refresh on route change
    const interval = setInterval(loadNotifications, 15000);
    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [user, location.pathname]);

  // Close notifications on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notifRef.current && !notifRef.current.contains(event.target)) {
        setNotifOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setNotifOpen(false);
  }, [location.pathname]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);

  const handleLogout = async () => {
    try {
      await signOut();
      setMobileMenuOpen(false);
      navigate('/');
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  // When notification bell is clicked, open panel and immediately reset unread count to 0
  const handleBellClick = async () => {
    const nextState = !notifOpen;
    setNotifOpen(nextState);
    if (nextState && user?.id && unreadCount > 0) {
      // Optimistically mark all notifications read locally
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      // Persist read status to Supabase without deleting notifications
      try {
        await markAllNotificationsAsRead(user.id);
      } catch (err) {
        console.warn('[Navbar] Mark all read notice:', err);
      }
    }
  };

  const handleMarkRead = async (notifId, e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    if (user?.id) {
      await markNotificationAsRead(notifId, user.id);
      setNotifications((prev) =>
        prev.map((n) => (n.id === notifId ? { ...n, is_read: true } : n))
      );
    }
  };

  const handleCollabAction = async (notif, action, e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    try {
      const collabId = notif.data?.collab_id;
      const senderId = notif.data?.sender_id;
      await respondToCollabRequest({
        collabId,
        notificationId: notif.id,
        action,
        senderId,
        recipientUser: user,
        recipientProfile: profile,
      });

      setNotifications((prev) =>
        prev.map((item) =>
          item.id === notif.id
            ? {
                ...item,
                is_read: true,
                data: { ...(item.data || {}), status: action },
              }
            : item
        )
      );
    } catch (err) {
      console.warn('[Navbar] Error responding to collab:', err);
    }
  };

  const navLinks = [
    { label: 'Home', path: '/' },
    { label: 'Services', path: '/services' },
    { label: 'Artist Web', path: '/artists' },
    { label: 'About', path: '/about' },
    { label: 'Contact', path: '/contact' },
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    if (path === '/artists') return location.pathname.startsWith('/artist');
    return location.pathname.startsWith(path);
  };

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  return (
    <header className={`senritsu-header ${scrolled ? 'scrolled' : ''}`}>
      <div className="header-content">
        {/* Logo */}
        <div className="logo-container">
          <Link to="/" aria-label="SenritsuX Home" className="logo-link">
            <img src={logo} alt="SenritsuX" className="logo" />
          </Link>
        </div>

        {/* Desktop and Tablet Nav Links */}
        <nav className="nav-menu nav-links-desktop" aria-label="Main Navigation">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`nav-link ${isActive(link.path) ? 'active' : ''}`}
              aria-current={isActive(link.path) ? 'page' : undefined}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        {/* Desktop Auth / CTA area (Right) */}
        <div className="social-links-header">
          {isAuthenticated ? (
            <div className="header-auth-group" style={{ position: 'relative' }}>
              {/* Notification Bell */}
              <div ref={notifRef} style={{ position: 'relative' }}>
                <button
                  type="button"
                  onClick={handleBellClick}
                  aria-label="Notifications"
                  style={{
                    background: notifOpen ? 'rgba(217, 251, 6, 0.15)' : 'rgba(255, 255, 255, 0.08)',
                    border: '1px solid rgba(255, 255, 255, 0.12)',
                    color: notifOpen ? '#d9fb06' : '#fff',
                    borderRadius: '50%',
                    width: '36px',
                    height: '36px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    position: 'relative',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <Bell size={17} />
                  {unreadCount > 0 && (
                    <span
                      style={{
                        position: 'absolute',
                        top: '-2px',
                        right: '-2px',
                        background: '#d9fb06',
                        color: '#111',
                        fontSize: '0.65rem',
                        fontWeight: 800,
                        borderRadius: '10px',
                        minWidth: '16px',
                        height: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '0 3px',
                      }}
                    >
                      {unreadCount}
                    </span>
                  )}
                </button>

                {/* Notifications Dropdown Panel */}
                {notifOpen && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 'calc(100% + 12px)',
                      right: 0,
                      width: '340px',
                      maxHeight: '420px',
                      background: '#322f87',
                      border: '1px solid rgba(217, 251, 6, 0.25)',
                      borderRadius: '12px',
                      boxShadow: '0 12px 35px rgba(0, 0, 0, 0.5)',
                      backdropFilter: 'blur(16px)',
                      zIndex: 1000,
                      display: 'flex',
                      flexDirection: 'column',
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        padding: '0.8rem 1rem',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <span style={{ fontWeight: 700, fontSize: '0.9rem', color: '#fff' }}>
                        Notifications
                      </span>
                      {unreadCount > 0 && (
                        <span style={{ fontSize: '0.75rem', color: '#d9fb06', fontWeight: 600 }}>
                          {unreadCount} Unread
                        </span>
                      )}
                    </div>

                    <div style={{ overflowY: 'auto', maxHeight: '340px', padding: '0.5rem' }}>
                      {notifications.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '1.5rem 1rem', color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.85rem' }}>
                          No notifications yet.
                        </div>
                      ) : (
                        notifications.map((n) => (
                          <div
                            key={n.id}
                            style={{
                              padding: '0.7rem 0.8rem',
                              borderRadius: '8px',
                              background: n.is_read ? 'transparent' : 'rgba(217, 251, 6, 0.08)',
                              borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                              display: 'flex',
                              gap: '0.6rem',
                              alignItems: 'flex-start',
                              transition: 'background 0.2s ease',
                            }}
                          >
                            <div
                              style={{
                                background: 'rgba(255, 255, 255, 0.1)',
                                borderRadius: '6px',
                                padding: '0.35rem',
                                color: n.is_read ? '#fff' : '#d9fb06',
                                flexShrink: 0,
                              }}
                            >
                              {n.type === 'distribution' ? <Radio size={15} /> : <Users2 size={15} />}
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: '0.82rem', fontWeight: 600, color: n.is_read ? '#fff' : '#d9fb06', marginBottom: '0.15rem' }}>
                                {n.title}
                              </div>
                              <div style={{ fontSize: '0.78rem', color: 'rgba(255, 255, 255, 0.85)', lineHeight: 1.35, marginBottom: '0.3rem' }}>
                                {n.message}
                              </div>

                              {/* Collab Request Recipient View: Social Links & Accept/Deny Buttons */}
                              {n.type === 'collab_request' && (
                                <div style={{ marginTop: '0.4rem', paddingTop: '0.4rem', borderTop: '1px solid rgba(255, 255, 255, 0.08)' }}>
                                  {/* Case A: Accepted Notification for User A (Requester) */}
                                  {n.title === 'COLLABORATION ACCEPTED' || n.data?.action === 'accepted' ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', marginTop: '0.2rem' }}>
                                      <span style={{ color: '#22c55e', fontSize: '0.75rem', fontWeight: 700 }}>
                                        ✓ COLLABORATION ACCEPTED
                                      </span>
                                      <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.85)', margin: 0 }}>
                                        {n.message}
                                      </p>
                                      {n.data?.partner_instagram && (
                                        <a
                                          href={n.data.partner_instagram}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          onClick={(e) => e.stopPropagation()}
                                          style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '0.3rem',
                                            background: 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)',
                                            color: '#fff',
                                            padding: '0.25rem 0.65rem',
                                            borderRadius: '4px',
                                            fontSize: '0.72rem',
                                            fontWeight: 700,
                                            textDecoration: 'none',
                                            width: 'fit-content',
                                          }}
                                        >
                                          Message on Instagram ↗
                                        </a>
                                      )}
                                    </div>
                                  ) : n.title === 'COLLABORATION DECLINED' || n.data?.action === 'denied' || n.data?.action === 'declined' ? (
                                    /* Case B: Declined Notification for User A (Requester) */
                                    <div style={{ marginTop: '0.2rem' }}>
                                      <span style={{ color: '#ef4444', fontSize: '0.75rem', fontWeight: 700 }}>
                                        ✕ COLLABORATION DECLINED
                                      </span>
                                      <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.7)', margin: '0.25rem 0 0' }}>
                                        {n.message}
                                      </p>
                                    </div>
                                  ) : (
                                    /* Case C: Incoming Collaboration Request for User B (Recipient) */
                                    <div>
                                      <div style={{ fontSize: '0.72rem', display: 'flex', flexDirection: 'column', gap: '0.2rem', marginBottom: '0.45rem' }}>
                                        <div>
                                          <strong style={{ color: 'rgba(255,255,255,0.6)' }}>Spotify: </strong>
                                          {n.data?.spotify_link ? (
                                            <a
                                              href={n.data.spotify_link}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              onClick={(e) => e.stopPropagation()}
                                              style={{ color: '#1ed760', textDecoration: 'underline' }}
                                            >
                                              Open Spotify Artist Page
                                            </a>
                                          ) : (
                                            <span style={{ color: 'rgba(255,255,255,0.45)' }}>Not provided</span>
                                          )}
                                        </div>
                                        <div>
                                          <strong style={{ color: 'rgba(255,255,255,0.6)' }}>YouTube: </strong>
                                          {n.data?.youtube ? (
                                            <a
                                              href={n.data.youtube}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              onClick={(e) => e.stopPropagation()}
                                              style={{ color: '#ff4b4b', textDecoration: 'underline' }}
                                            >
                                              Open YouTube
                                            </a>
                                          ) : (
                                            <span style={{ color: 'rgba(255,255,255,0.45)' }}>Not provided</span>
                                          )}
                                        </div>
                                        <div>
                                          <strong style={{ color: 'rgba(255,255,255,0.6)' }}>Instagram: </strong>
                                          {n.data?.instagram ? (
                                            <a
                                              href={n.data.instagram}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              onClick={(e) => e.stopPropagation()}
                                              style={{ color: '#e1306c', textDecoration: 'underline' }}
                                            >
                                              Open Instagram
                                            </a>
                                          ) : (
                                            <span style={{ color: 'rgba(255,255,255,0.45)' }}>Not provided</span>
                                          )}
                                        </div>
                                      </div>

                                      {n.data?.status === 'accepted' ? (
                                        <span style={{ color: '#22c55e', fontSize: '0.75rem', fontWeight: 700 }}>
                                          ✓ Accepted
                                        </span>
                                      ) : n.data?.status === 'denied' || n.data?.status === 'declined' ? (
                                        <span style={{ color: '#ef4444', fontSize: '0.75rem', fontWeight: 700 }}>
                                          ✕ Declined
                                        </span>
                                      ) : (
                                        <div style={{ display: 'flex', gap: '0.4rem', marginTop: '0.3rem' }}>
                                          <button
                                            type="button"
                                            onClick={(e) => handleCollabAction(n, 'accepted', e)}
                                            style={{
                                              background: '#22c55e',
                                              color: '#fff',
                                              border: 'none',
                                              borderRadius: '4px',
                                              padding: '0.25rem 0.65rem',
                                              fontSize: '0.72rem',
                                              fontWeight: 700,
                                              cursor: 'pointer',
                                            }}
                                          >
                                            ACCEPT
                                          </button>
                                          <button
                                            type="button"
                                            onClick={(e) => handleCollabAction(n, 'denied', e)}
                                            style={{
                                              background: 'rgba(255, 255, 255, 0.12)',
                                              color: '#fff',
                                              border: '1px solid rgba(255, 255, 255, 0.2)',
                                              borderRadius: '4px',
                                              padding: '0.25rem 0.65rem',
                                              fontSize: '0.72rem',
                                              fontWeight: 600,
                                              cursor: 'pointer',
                                            }}
                                          >
                                            DENY
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              )}

                              <div style={{ fontSize: '0.7rem', color: 'rgba(255, 255, 255, 0.4)', display: 'flex', alignItems: 'center', gap: '0.2rem', marginTop: '0.35rem' }}>
                                <Clock size={10} /> {formatNotificationTime(n.created_at)}
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>

              <Link to="/dashboard" className="nav-link nav-dashboard-link">
                Dashboard
              </Link>
              <button onClick={handleLogout} className="nav-link nav-logout-btn">
                Logout
              </button>
            </div>
          ) : (
            <div className="header-auth-group">
              <Link to="/login" className="nav-link nav-login-btn">
                Login
              </Link>
              <Link to="/signup" className="nav-signup-btn">
                Sign Up
              </Link>
            </div>
          )}
        </div>

        {/* Mobile Hamburger Toggle Button */}
        <button
          type="button"
          className="mobile-menu-toggle"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-expanded={mobileMenuOpen}
          aria-label={mobileMenuOpen ? 'Close menu' : 'Open navigation menu'}
        >
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu Dropdown Drawer */}
      {mobileMenuOpen && (
        <div className="mobile-menu-overlay" onClick={() => setMobileMenuOpen(false)}>
          <div
            className="mobile-menu-drawer"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Mobile Navigation"
          >
            <div className="mobile-menu-header">
              <img src={logo} alt="SenritsuX" className="mobile-menu-logo" />
              <button
                type="button"
                className="mobile-menu-close"
                onClick={() => setMobileMenuOpen(false)}
                aria-label="Close menu"
              >
                <X size={22} />
              </button>
            </div>

            <nav className="mobile-nav-links">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`mobile-nav-link ${isActive(link.path) ? 'active' : ''}`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <span>{link.label}</span>
                  {isActive(link.path) && <span className="mobile-nav-dot" />}
                </Link>
              ))}
            </nav>

            <div className="mobile-menu-footer">
              {isAuthenticated ? (
                <>
                  <Link
                    to="/dashboard"
                    className="mobile-cta-primary"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Go to Dashboard
                  </Link>
                  <button
                    type="button"
                    className="mobile-cta-secondary"
                    onClick={handleLogout}
                  >
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/signup"
                    className="mobile-cta-primary"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Join <span className="brand-font">SenritsuX</span> (Sign Up)
                  </Link>
                  <Link
                    to="/login"
                    className="mobile-cta-secondary"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Login to Your Account
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
